/**
 * Shop AI Chat - Client-side implementation
 *
 * This module handles the chat interface for the Shopify AI Chat application.
 * It manages the UI interactions, API communication, and message rendering.
 */
(function() {
  'use strict';

  /**
   * Application namespace to prevent global scope pollution
   */
  const ShopAIChat = {
    /**
     * UI-related elements and functionality
     */
    UI: {
      elements: {},
      isMobile: false,

      /**
       * Initialize UI elements and event listeners
       * @param {HTMLElement} container - The main container element
       */
      init: function(container) {
        if (!container) return;

        // Cache DOM elements
        this.elements = {
          container: container,
          chatBubble: container.querySelector('.shop-ai-chat-bubble'),
          chatWindow: container.querySelector('.shop-ai-chat-window'),
          closeButton: container.querySelector('.shop-ai-chat-close'),
          chatInput: container.querySelector('.shop-ai-chat-input input'),
          sendButton: container.querySelector('.shop-ai-chat-send'),
          messagesContainer: container.querySelector('.shop-ai-chat-messages')
        };

        // Detect mobile device
        this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        // ACCESSIBILITY (additive): mark the message stream as a polite live region so
        // screen readers announce streamed assistant replies, quiz cards, and product
        // results as they arrive. role="log" gives it the right semantics; aria-busy is
        // toggled by setBusy() during streaming. No markup change required in the snippet.
        if (this.elements.messagesContainer) {
          const mc = this.elements.messagesContainer;
          if (!mc.getAttribute('role')) mc.setAttribute('role', 'log');
          if (!mc.getAttribute('aria-live')) mc.setAttribute('aria-live', 'polite');
          mc.setAttribute('aria-relevant', 'additions text');
          mc.setAttribute('aria-atomic', 'false');
          mc.setAttribute('aria-busy', 'false');
        }

        if (this.elements.chatWindow) {
          const chatWindow = this.elements.chatWindow;
          chatWindow.setAttribute('role', 'dialog');
          chatWindow.setAttribute('aria-label', 'Mattress Overstock AI mattress guide');
          chatWindow.setAttribute('aria-hidden', 'true');
        }
        if (this.elements.chatBubble) {
          this.elements.chatBubble.setAttribute('aria-expanded', 'false');
        }

        // Set up event listeners
        this.setupEventListeners();

        // Fix for iOS Safari viewport height issues
        if (this.isMobile) {
          this.setupMobileViewport();
        }
      },

      /**
       * Set up all event listeners for UI interactions
       */
      setupEventListeners: function() {
        const { chatBubble, closeButton, chatInput, sendButton, messagesContainer } = this.elements;

        // Toggle chat window visibility
        chatBubble.addEventListener('click', () => this.toggleChatWindow());
        // Keyboard access for the launcher (the liquid gives it role=button tabindex=0)
        chatBubble.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.toggleChatWindow(); }
        });

        // Close chat window
        closeButton.addEventListener('click', () => this.closeChatWindow(true));

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.elements.chatWindow.classList.contains('active')) {
            this.closeChatWindow(true);
          }
        });

        // Send message when pressing Enter in input
        chatInput.addEventListener('keypress', (e) => {
          // Don't let Enter bypass the disabled send button mid-stream (double-send guard).
          if (e.key === 'Enter' && sendButton && sendButton.disabled) { e.preventDefault(); return; }
          if (e.key === 'Enter' && chatInput.value.trim() !== '') {
            ShopAIChat.Message.send(chatInput, messagesContainer);

            // On mobile, handle keyboard
            if (this.isMobile) {
              chatInput.blur();
              setTimeout(() => chatInput.focus(), 300);
            }
          }
        });

        // Send message when clicking send button
        sendButton.addEventListener('click', () => {
          if (chatInput.value.trim() !== '') {
            ShopAIChat.Message.send(chatInput, messagesContainer);

            // On mobile, focus input after sending
            if (this.isMobile) {
              setTimeout(() => chatInput.focus(), 300);
            }
          }
        });

        // Handle window resize to adjust scrolling
        window.addEventListener('resize', () => this.scrollToBottom());

        // Reader-intent tracking for the scroll guard: _pinned means "at/near the bottom
        // the last time anyone (reader or us) actually scrolled". Content appends don't
        // fire scroll events, so this survives big appends (product grids, finale prose) —
        // measuring the gap after an append cannot tell "reader scrolled up" from "tall
        // content just landed".
        this._pinned = true;
        messagesContainer.addEventListener('scroll', () => {
          const gap = messagesContainer.scrollHeight - messagesContainer.scrollTop - messagesContainer.clientHeight;
          this._pinned = gap <= 140;
        });

        // Add global click handler for auth links
        document.addEventListener('click', function(event) {
          if (event.target && event.target.classList.contains('shop-auth-trigger')) {
            event.preventDefault();
            if (window.shopAuthUrl) {
              ShopAIChat.Auth.openAuthPopup(window.shopAuthUrl);
            }
          }
        });
      },

      /**
       * Setup mobile-specific viewport adjustments
       */
      setupMobileViewport: function() {
        const setViewportHeight = () => {
          document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        };
        window.addEventListener('resize', setViewportHeight);
        setViewportHeight();
      },

      /**
       * Toggle chat window visibility
       */
      toggleChatWindow: function() {
        const { chatWindow, chatInput, chatBubble } = this.elements;

        chatWindow.classList.toggle('active');

        if (chatWindow.classList.contains('active')) {
          chatWindow.setAttribute('aria-hidden', 'false');
          if (chatBubble) chatBubble.setAttribute('aria-expanded', 'true');
          // A fresh chat should feel like the guide spoke first, not like a landing card
          // happened to be sitting behind the launcher.
          ShopAIChat.Quiz.showOpenGreeting(this.elements.messagesContainer);
          // Opening the chat pauses the nudge for this page-load only;
          // only the explicit ✕ dismiss silences it for the whole session.
          if (ShopAIChat.Nudge) ShopAIChat.Nudge.stop();
          // Hide the launcher while the window is open (it overlapped the input on mobile).
          if (chatBubble) chatBubble.classList.add('shop-ai-bubble-hidden');
          ShopAIChat.Analytics.push('chat_opened');
          // Identity-on-open: anonymous server-side touch + one silent signed-in
          // check per session. Fire-and-forget; never blocks or alters the open.
          if (ShopAIChat.Identity) ShopAIChat.Identity.onOpen();
          // On mobile, prevent body scrolling and delay focus
          if (this.isMobile) {
            document.body.classList.add('shop-ai-chat-open');
            setTimeout(() => chatInput.focus(), 500);
          } else {
            chatInput.focus();
          }
          // Always scroll messages to bottom when opening
          this.scrollToBottom(true);
        } else {
          chatWindow.setAttribute('aria-hidden', 'true');
          if (chatBubble) chatBubble.setAttribute('aria-expanded', 'false');
          if (chatBubble) chatBubble.classList.remove('shop-ai-bubble-hidden');
          // Remove body class when closing
          document.body.classList.remove('shop-ai-chat-open');
        }
      },

      /**
       * Close chat window
       */
      closeChatWindow: function(returnFocus) {
        const { chatWindow, chatInput, chatBubble } = this.elements;

        chatWindow.classList.remove('active');
        chatWindow.setAttribute('aria-hidden', 'true');
        if (chatBubble) {
          chatBubble.setAttribute('aria-expanded', 'false');
          chatBubble.classList.remove('shop-ai-bubble-hidden');
        }

        // On mobile, blur input to hide keyboard and enable body scrolling
        if (this.isMobile) {
          chatInput.blur();
          document.body.classList.remove('shop-ai-chat-open');
        }
        if (returnFocus && chatBubble) chatBubble.focus();
      },

      /**
       * Scroll messages container to bottom.
       * @param {boolean} force - true for user-initiated moments (open, send, form/quiz shown):
       * always scroll. Streaming/layout events stay guarded: if the reader deliberately
       * scrolled up (see _pinned), don't yank them back down.
       */
      scrollToBottom: function(force) {
        const { messagesContainer } = this.elements;
        setTimeout(() => {
          if (!force && this._pinned === false) return;
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }, 100);
      },

      /**
       * Keep a conversion form fully in view. Product cards can land after the form,
       * so bottom-scrolling alone can hide the fields above the visible pane.
       * @param {HTMLElement} element
       * @param {boolean} force
       */
      scrollOfferIntoView: function(element, force) {
        const { messagesContainer } = this.elements;
        if (!messagesContainer || !element) return;
        setTimeout(() => {
          if (!force && this._pinned === false) return;
          const margin = 12;
          const usableHeight = messagesContainer.clientHeight - (margin * 2);
          const targetTop = element.offsetHeight > usableHeight
            ? Math.max(0, element.offsetTop - margin)
            : Math.max(0, element.offsetTop + element.offsetHeight + margin - messagesContainer.clientHeight);
          messagesContainer.scrollTop = targetTop;
          this._pinned = true;
        }, 120);
      },

      /**
       * Toggle the "streaming in progress" state on the composer.
       * ADDITIVE: disables + spins the send button while a reply streams so the
       * shopper can't double-send (which would interleave two SSE streams into
       * the same message container). Also flags the live region as busy for AT.
       * Idempotent and defensive — never throws, never blocks the stream.
       * @param {boolean} busy
       */
      setBusy: function(busy) {
        try {
          const send = this.elements.sendButton;
          const input = this.elements.chatInput;
          const messages = this.elements.messagesContainer;
          if (send) {
            send.classList.toggle('is-busy', !!busy);
            send.disabled = !!busy;
            send.setAttribute('aria-busy', busy ? 'true' : 'false');
          }
          // Keep the input usable for typing the next question, but reflect state.
          if (input) input.classList.toggle('shop-ai-input-busy', !!busy);
          if (messages) messages.setAttribute('aria-busy', busy ? 'true' : 'false');
        } catch (e) { /* non-fatal */ }
      },

      /**
       * Show typing indicator in the chat
       */
      showTypingIndicator: function() {
        const { messagesContainer } = this.elements;

        const typingIndicator = document.createElement('div');
        typingIndicator.classList.add('shop-ai-typing-indicator');
        typingIndicator.innerHTML = '<span></span><span></span><span></span>';
        messagesContainer.appendChild(typingIndicator);
        this.scrollToBottom();
      },

      /**
       * Remove typing indicator from the chat
       */
      removeTypingIndicator: function() {
        const { messagesContainer } = this.elements;

        const typingIndicator = messagesContainer.querySelector('.shop-ai-typing-indicator');
        if (typingIndicator) {
          typingIndicator.remove();
        }
      },

      /**
       * Display product results in the chat
       * @param {Array} products - Array of product data objects
       */
      displayProductResults: function(products) {
        const { messagesContainer } = this.elements;
        const activeLeadForm = messagesContainer.querySelector('form.shop-ai-lead-form:not(.shop-ai-support-form):not(.shop-ai-lead-done)');

        // Create a wrapper for the product section
        const productSection = document.createElement('div');
        productSection.classList.add('shop-ai-product-section');
        messagesContainer.appendChild(productSection);

        // Add a header for the product results
        const header = document.createElement('div');
        header.classList.add('shop-ai-product-header');
        header.innerHTML = '<h4>Top matches for you</h4>';
        productSection.appendChild(header);

        // Create the product grid container
        const productsContainer = document.createElement('div');
        productsContainer.classList.add('shop-ai-product-grid');
        productSection.appendChild(productsContainer);

        if (!products || !Array.isArray(products) || products.length === 0) {
          const noProductsMessage = document.createElement('p');
          noProductsMessage.textContent = "No products found";
          noProductsMessage.style.padding = "10px";
          productsContainer.appendChild(noProductsMessage);
        } else {
          // Pull the just-finished assistant reply so each card can show the bot's own
          // "why it fits you" line. product_results fires around end_turn, so the latest
          // NON-EMPTY assistant bubble holds the complete reply with the per-bed lines
          // (scan from the end, skipping any trailing empty/placeholder bubble).
          const assistantEls = messagesContainer.querySelectorAll('.shop-ai-message.assistant');
          let lastReply = '';
          for (let k = assistantEls.length - 1; k >= 0; k--) {
            const txt = assistantEls[k].dataset.rawText || assistantEls[k].textContent || '';
            if (txt.trim()) { lastReply = txt; break; }
          }
          const reasons = ShopAIChat.Product.extractReasons(lastReply);
          products.forEach(product => {
            const reason = ShopAIChat.Product.reasonFor(product.title, reasons);
            const productCard = ShopAIChat.Product.createCard(product, reason);
            productsContainer.appendChild(productCard);
          });
          // Deterministic capture safety net: with a real shortlist on screen and no lead
          // form anywhere in the thread (active OR submitted), offer to text the picks —
          // shopper-tap-initiated, so it works even when the model never offers.
          if (products.length >= 1 && !sessionStorage.getItem('shopAiLeadDone') &&
              !sessionStorage.getItem('shopAiSupportActive') &&
              !messagesContainer.querySelector('form.shop-ai-lead-form:not(.shop-ai-support-form)') &&
              !messagesContainer.querySelector('.shop-ai-support-form')) {
            const financingContext = /financ|monthly[- ]payment|synchrony/i.test(lastReply);
            const fallbackHook = financingContext ? 'financing' : 'save_cart';
            const pickLabel = products.length === 1 ? 'Save this pick' : 'Save these picks';
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'shop-ai-text-picks-chip';
            chip.textContent = financingContext ? 'See payment options' : pickLabel;
            chip.addEventListener('click', () => {
              ShopAIChat.Analytics.push('chip_text_picks_clicked');
              chip.remove();
              ShopAIChat.Lead.show(fallbackHook, messagesContainer);
            });
            productSection.appendChild(chip);
          }

          // Carousel scroll affordance (ADDITIVE, visual-only): toggle .is-scrollable
          // on the section once the row actually overflows, and .at-end when scrolled
          // to the end — the CSS shows/retracts the right-edge fade + "→" cue off these.
          // A single card (or a non-overflowing row) never gets .is-scrollable, so it
          // shows no peek/fade. No-op on the mobile vertical stack (no x-overflow).
          this.wireCarouselCue(productSection, productsContainer);
        }

        if (activeLeadForm) {
          messagesContainer.appendChild(activeLeadForm);
          ShopAIChat.Lead.applyCopy(activeLeadForm, activeLeadForm.dataset.hook || 'save_cart', {
            productCount: Array.isArray(products) ? products.length : 0
          });
          activeLeadForm.classList.add('shop-ai-lead-form-reanchored');
          ShopAIChat.UI.scrollOfferIntoView(activeLeadForm, true);
          return;
        }

        this.scrollToBottom();
      },

      /**
       * Toggle the scroll-affordance classes on a product section based on whether its
       * grid overflows horizontally and where it's scrolled. Purely additive/visual —
       * never alters markup or card behavior.
       * @param {HTMLElement} section - .shop-ai-product-section
       * @param {HTMLElement} grid - .shop-ai-product-grid (the scroll container)
       */
      wireCarouselCue: function(section, grid) {
        if (!section || !grid) return;

        const SLOP = 4; // px tolerance for sub-pixel rounding
        const update = function() {
          const overflow = grid.scrollWidth - grid.clientWidth;
          if (overflow > SLOP) {
            section.classList.add('is-scrollable');
            const atEnd = grid.scrollLeft >= (overflow - SLOP);
            section.classList.toggle('at-end', atEnd);
          } else {
            // Not scrollable (single card / fits) — no cue at all.
            section.classList.remove('is-scrollable');
            section.classList.remove('at-end');
          }
        };

        grid.addEventListener('scroll', update, { passive: true });

        // Re-measure on viewport changes (window resize, mobile rotate). Stored on the
        // grid so it can be cleaned conceptually; harmless if the node is removed.
        if (typeof ResizeObserver === 'function') {
          const ro = new ResizeObserver(update);
          ro.observe(grid);
        } else {
          window.addEventListener('resize', update, { passive: true });
        }

        // Initial measure: now, and again after images load / layout settles.
        update();
        requestAnimationFrame(update);
        setTimeout(update, 120);
      }
    },

    /**
     * Message handling and display functionality
     */
    Message: {
      /**
       * Send a message to the API
       * @param {HTMLInputElement} chatInput - The input element
       * @param {HTMLElement} messagesContainer - The messages container
       */
      send: async function(chatInput, messagesContainer) {
        ShopAIChat.Quiz.removeWelcomeChip();
        const userMessage = chatInput.value.trim();
        const conversationId = sessionStorage.getItem('shopAiConversationId');

        // Add user message to chat
        this.add(userMessage, 'user', messagesContainer);

        // Clear input
        chatInput.value = '';

        // Show typing indicator + lock the composer for the duration of the stream
        ShopAIChat.UI.showTypingIndicator();
        ShopAIChat.UI.setBusy(true);

        try {
          ShopAIChat.API.streamResponse(userMessage, conversationId, messagesContainer);
        } catch (error) {
          console.error('Error communicating with Claude API:', error);
          ShopAIChat.UI.removeTypingIndicator();
          ShopAIChat.UI.setBusy(false);
          this.add("Sorry, I couldn't process your request at the moment. Please try again later.", 'assistant', messagesContainer);
        }
      },

      /**
       * Add a message to the chat
       * @param {string} text - Message content
       * @param {string} sender - Message sender ('user' or 'assistant')
       * @param {HTMLElement} messagesContainer - The messages container
       * @returns {HTMLElement} The created message element
       */
      add: function(text, sender, messagesContainer) {
        const messageElement = document.createElement('div');
        messageElement.classList.add('shop-ai-message', sender);

        if (sender === 'assistant') {
          messageElement.dataset.rawText = text;
          ShopAIChat.Formatting.formatMessageContent(messageElement);
        } else {
          messageElement.textContent = text;
        }

        messagesContainer.appendChild(messageElement);
        ShopAIChat.UI.scrollToBottom(true);

        return messageElement;
      },

      /**
       * Add a tool use message to the chat with expandable arguments
       * @param {string} toolMessage - Tool use message content
       * @param {HTMLElement} messagesContainer - The messages container
       */
      addToolUse: function(toolMessage, messagesContainer) {
        // Parse the tool message to extract tool name and arguments
        const match = toolMessage.match(/Calling tool: (\w+) with arguments: (.+)/);
        if (!match) {
          // Fallback for unexpected format
          const toolUseElement = document.createElement('div');
          toolUseElement.classList.add('shop-ai-message', 'tool-use');
          toolUseElement.textContent = toolMessage;
          messagesContainer.appendChild(toolUseElement);
          ShopAIChat.UI.scrollToBottom();
          return;
        }

        const toolName = match[1];
        const argsString = match[2];

        // Create the main tool use element
        const toolUseElement = document.createElement('div');
        toolUseElement.classList.add('shop-ai-message', 'tool-use');

        // Create the header (always visible)
        const headerElement = document.createElement('div');
        headerElement.classList.add('shop-ai-tool-header');

        const toolText = document.createElement('span');
        toolText.classList.add('shop-ai-tool-text');
        toolText.textContent = `Calling tool: ${toolName}`;

        const toggleElement = document.createElement('span');
        toggleElement.classList.add('shop-ai-tool-toggle');
        toggleElement.textContent = '[+]';

        headerElement.appendChild(toolText);
        headerElement.appendChild(toggleElement);

        // Create the arguments section (initially hidden)
        const argsElement = document.createElement('div');
        argsElement.classList.add('shop-ai-tool-args');

        try {
          // Try to format JSON arguments nicely
          const parsedArgs = JSON.parse(argsString);
          argsElement.textContent = JSON.stringify(parsedArgs, null, 2);
        } catch (e) {
          // If not valid JSON, just show as-is
          argsElement.textContent = argsString;
        }

        // Add click handler to toggle arguments visibility
        headerElement.addEventListener('click', function() {
          const isExpanded = argsElement.classList.contains('expanded');
          if (isExpanded) {
            argsElement.classList.remove('expanded');
            toggleElement.textContent = '[+]';
          } else {
            argsElement.classList.add('expanded');
            toggleElement.textContent = '[-]';
          }
        });

        // Assemble the complete element
        toolUseElement.appendChild(headerElement);
        toolUseElement.appendChild(argsElement);

        messagesContainer.appendChild(toolUseElement);
        ShopAIChat.UI.scrollToBottom();
      }
    },

    /**
     * Text formatting and markdown handling
     */
    Formatting: {
      /**
       * Format message content with markdown and links
       * @param {HTMLElement} element - The element to format
       */
      /** Escape HTML so model/catalog text can never inject markup into innerHTML. */
      escapeHtml: function(s) {
        return String(s == null ? '' : s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      },

      /** Only allow safe link schemes; anything else (javascript:, data:, etc.) -> '#'. */
      safeUrl: function(url) {
        const u = String(url == null ? '' : url).trim();
        return /^(https?:\/\/|\/|mailto:|#)/i.test(u) ? u : '#';
      },

      formatMessageContent: function(element) {
        if (!element || !element.dataset.rawText) return;

        // SECURITY: escape the raw model text FIRST so any HTML it (or injected catalog
        // data) contains is neutralized before we add our own safe markup below.
        let processedText = this.escapeHtml(element.dataset.rawText);

        // Process Markdown links. Link text is already escaped; sanitize the URL scheme.
        const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        processedText = processedText.replace(markdownLinkRegex, (match, text, url) => {
          const safe = this.safeUrl(url);
          if (url.includes('shopify.com/authentication') &&
             (url.includes('oauth/authorize') || url.includes('authentication'))) {
            window.shopAuthUrl = safe;
            return '<a href="#auth" class="shop-auth-trigger">' + text + '</a>';
          } else if (url.includes('/cart') || url.includes('checkout')) {
            return '<a href="' + safe + '" target="_blank" rel="noopener noreferrer">click here to proceed to checkout</a>';
          } else {
            return '<a href="' + safe + '" target="_blank" rel="noopener noreferrer">' + text + '</a>';
          }
        });

        // Convert the (already-escaped) text to HTML with bold/list handling.
        processedText = this.convertMarkdownToHtml(processedText);

        element.innerHTML = processedText;
      },

      /**
       * Convert Markdown text to HTML with list support
       * @param {string} text - Markdown text to convert
       * @returns {string} HTML content
       */
      convertMarkdownToHtml: function(text) {
        text = text.replace(/(\*\*|__)(.*?)\1/g, '<strong>$2</strong>');
        const lines = text.split('\n');
        let currentList = null;
        let listItems = [];
        let htmlContent = '';
        let startNumber = 1;

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const unorderedMatch = line.match(/^\s*([-*])\s+(.*)/);
          const orderedMatch = line.match(/^\s*(\d+)[\.)]\s+(.*)/);

          if (unorderedMatch) {
            if (currentList !== 'ul') {
              if (currentList === 'ol') {
                htmlContent += `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
                listItems = [];
              }
              currentList = 'ul';
            }
            listItems.push('<li>' + unorderedMatch[2] + '</li>');
          } else if (orderedMatch) {
            if (currentList !== 'ol') {
              if (currentList === 'ul') {
                htmlContent += '<ul>' + listItems.join('') + '</ul>';
                listItems = [];
              }
              currentList = 'ol';
              startNumber = parseInt(orderedMatch[1], 10);
            }
            listItems.push('<li>' + orderedMatch[2] + '</li>');
          } else {
            if (currentList) {
              htmlContent += currentList === 'ul'
                ? '<ul>' + listItems.join('') + '</ul>'
                : `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
              listItems = [];
              currentList = null;
            }

            if (line.trim() === '') {
              htmlContent += '<br>';
            } else {
              htmlContent += '<p>' + line + '</p>';
            }
          }
        }

        if (currentList) {
          htmlContent += currentList === 'ul'
            ? '<ul>' + listItems.join('') + '</ul>'
            : `<ol start="${startNumber}">` + listItems.join('') + '</ol>';
        }

        htmlContent = htmlContent.replace(/<\/p><p>/g, '</p>\n<p>');
        return htmlContent;
      }
    },

    PageContext: {
      handle: function() {
        const m = window.location.pathname.match(/\/products\/([a-z0-9-]+)/);
        return m ? m[1] : null;
      },
      shortTitle: function() {
        // Product headings can hydrate after the widget, while social metadata is ready in the initial HTML.
        const heading = Array.from(document.querySelectorAll(
          '.product__title h1, h1.product__title, [data-product-title], main h1'
        )).map((el) => String(el.textContent || '').replace(/\s+/g, ' ').trim()).find(Boolean);
        const ogTitle = document.querySelector('meta[property="og:title"]');
        const twitterTitle = document.querySelector('meta[name="twitter:title"]');
        const handleTitle = (this.handle() || '').split('-').filter(Boolean)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
        const raw = heading ||
          (ogTitle && ogTitle.getAttribute('content')) ||
          (twitterTitle && twitterTitle.getAttribute('content')) ||
          document.title ||
          handleTitle;
        let title = String(raw || '')
          .replace(/\s+/g, ' ')
          .trim()
          .split(/\s*[|–—]\s*Mattress Overstock.*$/i)[0]
          .trim();
        if (title.includes(':')) title = title.split(':')[0].trim();
        if (title.length <= 54) return title.length > 1 ? title : null;
        const clipped = title.slice(0, 55).replace(/\s+\S*$/, '').trim();
        return clipped.length > 1 ? clipped : null;
      },
      safeUrl: function() {
        try {
          const url = new URL(window.location.href);
          // Page path is enough for catalog/context. Query and fragment may carry
          // a customer identifier and are never needed by the Chat model.
          url.search = '';
          url.hash = '';
          return url.toString().slice(0, 512);
        } catch (e) {
          return window.location.origin + window.location.pathname;
        }
      },
      get: function() {
        return {
          url: this.safeUrl(),
          title: (document.title || '').slice(0, 256),
          handle: this.handle()
        };
      }
    },

    Cookies: {
      read: function(name) {
        const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
      },
      // First-party analytics ids; stitch server-side events to this browser session.
      collect: function() {
        const out = {};
        try {
          const ga = this.read('_ga'); // "GA1.1.123456.789" -> "123456.789"
          if (ga) { const m = ga.match(/GA\d+\.\d+\.(.+)$/); if (m) out.ga_client_id = m[1].slice(0, 64); }
          const fbp = this.read('_fbp'); if (fbp) out.fbp = fbp.slice(0, 128);
          const fbc = this.read('_fbc'); if (fbc) out.fbc = fbc.slice(0, 256);
        } catch (e) { /* non-fatal */ }
        return out;
      }
    },

    Analytics: {
      // GTM-ready funnel events. No PII, ever.
      push: function(event, params) {
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(Object.assign({ event: event, pdp: !!ShopAIChat.PageContext.handle() }, params || {}));
        } catch (e) { /* non-fatal */ }
        if (ShopAIChat.Funnel) ShopAIChat.Funnel.track(event, params || {});
      }
    },

    GoogleUserData: {
      // Enhanced conversions for leads (2026-10-15). After a lead or quiz-gate
      // submit succeeds, hand the Google tag the shopper's SHA-256 hashed email
      // and phone, so Google can match later store-sale uploads to the ad click.
      // Only hashes leave this function (gtag queues them in dataLayer, as it
      // does every gtag call). The Google tag drops user data when ad_user_data
      // consent is denied.
      TAG: 'AW-310848106',
      normEmail: function(value) {
        const raw = String(value || '').trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return null;
        const at = raw.lastIndexOf('@');
        let local = raw.slice(0, at);
        const domain = raw.slice(at + 1);
        if (domain === 'gmail.com' || domain === 'googlemail.com') local = local.replace(/\./g, '');
        return local + '@' + domain;
      },
      normPhone: function(value) {
        const digits = String(value || '').replace(/\D/g, '');
        if (digits.length === 10) return '+1' + digits;
        if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
        return null;
      },
      sha256: async function(text) {
        const bytes = new TextEncoder().encode(text);
        const digest = await window.crypto.subtle.digest('SHA-256', bytes);
        return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
      },
      send: async function(email, phone, source) {
        try {
          if (typeof window.gtag !== 'function' || !window.crypto || !window.crypto.subtle) return false;
          const userData = {};
          const normEmail = this.normEmail(email);
          const normPhone = this.normPhone(phone);
          if (normEmail) userData.sha256_email_address = await this.sha256(normEmail);
          if (normPhone) userData.sha256_phone_number = await this.sha256(normPhone);
          if (!userData.sha256_email_address && !userData.sha256_phone_number) return false;
          window.gtag('set', 'user_data', userData);
          window.gtag('event', 'form_submit', { send_to: this.TAG, form_id: 'mo-chat-' + (source || 'lead') });
          return true;
        } catch (e) { return false; /* non-fatal */ }
      }
    },

    Attribution: {
      KEY: 'shopAiAttribution',
      CLICK_IDS: ['gclid','gbraid','wbraid','fbclid'],
      safeValue: function(value) {
        const raw = String(value || '').trim().slice(0, 256);
        if (!raw) return null;
        let decoded = raw;
        try { decoded = decodeURIComponent(raw); } catch (e) { /* keep raw */ }
        const email = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
        const phone = /(?:\+?1[ .-]?)?(?:\([2-9]\d{2}\)|[2-9]\d{2})[ .-]?\d{3}[ .-]?\d{4}/;
        return email.test(decoded) || phone.test(decoded) ? null : raw;
      },
      safeCurrentUrl: function() {
        try {
          const url = new URL(window.location.href);
          url.search = '';
          url.hash = '';
          return url.toString().slice(0, 512);
        } catch (e) { return null; }
      },
      safeReferrerOrigin: function() {
        try {
          const url = new URL(document.referrer || '');
          return url.protocol === 'https:' ? url.origin : null;
        } catch (e) { return null; }
      },
      capture: function() {
        try {
          const p = new URLSearchParams(window.location.search);
          const pick = ['gclid','gbraid','wbraid','fbclid','utm_source','utm_medium','utm_campaign','utm_term','utm_content'];
          const cur = {};
          pick.forEach((k) => { const v = this.safeValue(p.get(k)); if (v) cur[k] = v; });
          const existing = this.get();
          const hasStored = existing && Object.keys(existing).length > 0;
          const storedHasClickId = this.CLICK_IDS.some((k) => existing && existing[k]);
          const curHasClickId = this.CLICK_IDS.some((k) => cur[k]);
          // Keep first-touch UTM/landing, but a click id ALWAYS wins: if this
          // visit carries a gclid/gbraid/wbraid/fbclid and the stored record has
          // none, upgrade it in place — otherwise a returning visitor whose FIRST
          // touch was organic would permanently shadow a later ad click, and its
          // sale would never export to the Google offline-conversion pipeline.
          if (!hasStored) {
            localStorage.setItem(this.KEY, JSON.stringify({ ...cur, landing_url: this.safeCurrentUrl(), referrer: this.safeReferrerOrigin(), ts: Date.now() }));
          } else if (curHasClickId && !storedHasClickId) {
            const merged = { ...existing };
            this.CLICK_IDS.forEach((k) => { if (cur[k]) merged[k] = cur[k]; });
            merged.click_url = this.safeCurrentUrl();
            merged.click_ts = Date.now();
            localStorage.setItem(this.KEY, JSON.stringify(merged));
          }
        } catch (e) { /* storage disabled — non-fatal */ }
      },
      get: function() {
        try { return JSON.parse(localStorage.getItem(this.KEY) || '{}'); } catch (e) { return {}; }
      }
    },

    Funnel: {
      BACKEND: 'https://mo-shop-chat-agent-production.up.railway.app/funnel',
      KEY: 'shopAiFunnelId',
      EVENTS: ['quiz_started', 'quiz_question_shown', 'lead_gate_shown', 'lead_form_started', 'results_unlocked', 'brand_hint_revealed', 'timeline_selected'],
      init: function() {
        try {
          if (sessionStorage.getItem(this.KEY)) return;
          const token = window.crypto && typeof window.crypto.randomUUID === 'function'
            ? window.crypto.randomUUID()
            : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 14);
          sessionStorage.setItem(this.KEY, 'f-' + token);
        } catch (e) { /* storage disabled - analytics remains non-fatal */ }
      },
      id: function() {
        try { return sessionStorage.getItem(this.KEY); } catch (e) { return null; }
      },
      deviceType: function() {
        const width = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
        if (width <= 767) return 'mobile';
        if (width <= 1024) return 'tablet';
        return 'desktop';
      },
      trafficSource: function() {
        const a = ShopAIChat.Attribution.get();
        const source = String(a.utm_source || '').toLowerCase();
        const medium = String(a.utm_medium || '').toLowerCase();
        const referrer = String(a.referrer || '').toLowerCase();
        if (a.gclid || a.gbraid || a.wbraid) return 'google_ads';
        if (a.fbclid || /facebook|instagram|meta/.test(source)) return 'meta_ads';
        if (/cpc|ppc|paid|display/.test(medium)) return 'other_paid';
        if (/email/.test(medium) || /email|newsletter/.test(source)) return 'email';
        if (/organic/.test(medium) || /google|bing|duckduckgo|yahoo/.test(referrer)) return 'organic_search';
        if (/referral/.test(medium) || (referrer && !/mattressoverstock\.us/.test(referrer))) return 'referral';
        if (!source && !medium && !referrer) return 'direct';
        return source || medium ? 'other' : 'unknown';
      },
      track: function(name, properties) {
        if (!this.EVENTS.includes(name)) return;
        const funnelId = this.id();
        if (!funnelId) return;
        try {
          fetch(this.BACKEND, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({
              funnel_id: funnelId,
              conversation_id: sessionStorage.getItem('shopAiConversationId'),
              traffic_source: this.trafficSource(),
              device_type: this.deviceType(),
              events: [{ name: name, properties: properties || {}, occurred_at: new Date().toISOString() }]
            })
          }).catch(function() {});
        } catch (e) { /* non-fatal */ }
      }
    },

    /**
     * Identity-on-open. On a deliberate chat open this first persists the anonymous
     * server-side Chat touch (the attribution bundle the widget already collects —
     * never a name/email/phone). Only after that write succeeds does it attempt ONE
     * silent Shopify customer-account check per session through a
     * hidden iframe (OAuth prompt=none). A signed-in shopper gets linked
     * server-side; everyone else fails closed to anonymous chat with no login
     * prompt, popup, or visible change. No PII ever enters widget code, storage,
     * dataLayer, or analytics from this flow — the iframe's postMessage result is
     * only {type:'mo-identity', ok, reason}.
     */
    Identity: {
      TOUCH_BACKEND: 'https://mo-shop-chat-agent-production.up.railway.app/touch',
      SILENT_BACKEND: 'https://mo-shop-chat-agent-production.up.railway.app/auth/silent',
      BACKEND_ORIGIN: 'https://mo-shop-chat-agent-production.up.railway.app',
      DONE_KEY: 'shopAiIdentityDone',
      TRIED_KEY: 'shopAiIdentityTried',
      _touchSentThisLoad: false,
      _triedThisLoad: false,
      onOpen: function() {
        const self = this;
        try {
          Promise.resolve(this.touch()).then(function(saved) {
            if (saved) self.silentAuth();
          }).catch(function() {});
        } catch (e) { /* non-fatal */ }
      },
      // Anonymous touch: one POST per page load; the server upserts one row per
      // funnel session (repeat opens bump a counter server-side).
      touch: function() {
        if (this._touchSentThisLoad) return Promise.resolve(false);
        try {
          const funnelId = ShopAIChat.Funnel.id();
          if (!funnelId) return Promise.resolve(false); // storage disabled: no durable key to record against
          this._touchSentThisLoad = true;
          let conversationId = null;
          try { conversationId = sessionStorage.getItem('shopAiConversationId'); } catch (e) { /* non-fatal */ }
          return fetch(this.TOUCH_BACKEND, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({
              funnel_id: funnelId,
              conversation_id: conversationId,
              attribution: Object.assign({}, ShopAIChat.Attribution.get(), ShopAIChat.Cookies.collect()),
              page: ShopAIChat.PageContext.get(),
              device_type: ShopAIChat.Funnel.deviceType(),
              widget_version: document.querySelector('.shop-ai-chat-container')?.dataset.moVersion || null
            })
          }).then(function(response) { return !!response.ok; }).catch(function() { return false; });
        } catch (e) { return Promise.resolve(false); }
      },
      // Silent signed-in check: hidden iframe -> backend /auth/silent -> Shopify
      // authorize (prompt=none) -> backend callback page -> postMessage back here.
      // Once per session (sessionStorage guard; in-memory fallback when storage is
      // blocked). The iframe is removed on the result message or a 12s timeout.
      silentAuth: function() {
        if (this._triedThisLoad) return;
        this._triedThisLoad = true;
        try {
          const funnelId = ShopAIChat.Funnel.id();
          if (!funnelId) return; // identity must key to the same funnel session as the touch
          try {
            if (sessionStorage.getItem(this.DONE_KEY) || sessionStorage.getItem(this.TRIED_KEY)) return;
            sessionStorage.setItem(this.TRIED_KEY, '1');
          } catch (e) { /* storage disabled — the per-page-load guard above still applies */ }
          let conversationId = '';
          try { conversationId = sessionStorage.getItem('shopAiConversationId') || ''; } catch (e) { /* non-fatal */ }
          const src = this.SILENT_BACKEND +
            '?funnel_id=' + encodeURIComponent(funnelId) +
            (conversationId ? '&conversation_id=' + encodeURIComponent(conversationId) : '') +
            '&shop=' + encodeURIComponent(window.location.origin);
          const iframe = document.createElement('iframe');
          iframe.src = src;
          iframe.setAttribute('aria-hidden', 'true');
          iframe.setAttribute('tabindex', '-1');
          iframe.setAttribute('title', '');
          iframe.style.cssText = 'display:none;width:0;height:0;border:0;position:absolute;';
          const self = this;
          let settled = false;
          const onMessage = function(event) {
            if (event.origin !== self.BACKEND_ORIGIN) return;
            const data = event.data;
            if (!data || data.type !== 'mo-identity') return;
            if (data.ok) {
              try { sessionStorage.setItem(self.DONE_KEY, '1'); } catch (e) { /* non-fatal */ }
            }
            cleanup();
          };
          const cleanup = function() {
            if (settled) return;
            settled = true;
            window.removeEventListener('message', onMessage);
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
          };
          window.addEventListener('message', onMessage);
          setTimeout(cleanup, 12000);
          document.body.appendChild(iframe);
        } catch (e) { /* non-fatal — the chat open is never affected */ }
      }
    },

    Lead: {
      BACKEND: 'https://mo-shop-chat-agent-production.up.railway.app/lead',
      copy: {
        save_cart:  {
          title: "Want to save this shortlist with exact size prices?",
          subtitle: "We will send a clean copy for the size and beds you are considering.",
          btn: "Save this shortlist"
        },
        price_drop: {
          title: "Want our team to verify whether a current offer applies?",
          subtitle: "They can confirm any verified promotion for the bed and size you are considering.",
          btn: "Verify current offers"
        },
        first_dibs: {
          title: "Want a heads-up when a verified offer becomes available?",
          subtitle: "Our team can follow up if a confirmed offer matches the beds you are considering.",
          btn: "Get verified updates"
        },
        financing:  {
          title: "Want real monthly-payment options sent to you?",
          subtitle: "A Mattress Overstock team member can send options for the bed you are considering.",
          btn: "Send payment options"
        }
      },
      consentText: "I agree to receive recurring automated marketing & cart-reminder texts from Mattress Overstock at the number above. Consent isn't a condition of purchase. Msg & data rates may apply. Reply STOP to opt out, HELP for help.",
      copyFor: function(hook, context) {
        const key = this.copy[hook] ? hook : 'save_cart';
        if (key === 'save_cart' && context && Number(context.productCount) > 0) {
          const count = Number(context.productCount);
          const label = count === 1 ? 'pick' : 'picks';
          return {
            title: "Want these " + label + " saved with exact size prices?",
            subtitle: "We will send a clean shortlist for the size and beds you are considering.",
            btn: "Save these " + label
          };
        }
        return this.copy[key];
      },
      applyCopy: function(form, hook, context) {
        if (!form) return;
        const c = this.copyFor(hook, context);
        const title = form.querySelector('.shop-ai-lead-title');
        const subtitle = form.querySelector('.shop-ai-lead-subtitle');
        const submit = form.querySelector('.shop-ai-lead-submit');
        if (title) title.textContent = c.title;
        if (subtitle) subtitle.textContent = c.subtitle || '';
        if (submit) submit.textContent = c.btn;
      },
      show: function(hook, messagesContainer, opts) {
        // Already captured this session (survives reloads) — never re-offer a form.
        if (sessionStorage.getItem('shopAiLeadDone')) return;
        // A support handoff owns the conversation; never stack a sales form beside it.
        if (sessionStorage.getItem('shopAiSupportActive') || messagesContainer.querySelector('.shop-ai-support-form')) return;
        // Already captured in this thread — never stack another form on a submitted one.
        if (messagesContainer.querySelector('form.shop-ai-lead-form.shop-ai-lead-done')) return;
        // An active (unsubmitted) lead form: re-anchor it under the newest offer instead of
        // silently dropping the offer. (:not(.shop-ai-support-form) — the support form shares
        // the lead-form class and used to block lead forms entirely.)
        const existing = messagesContainer.querySelector('form.shop-ai-lead-form:not(.shop-ai-support-form):not(.shop-ai-lead-done)');
        if (existing) {
          existing.dataset.hook = hook;
          this.applyCopy(existing, hook);
          messagesContainer.appendChild(existing);
          ShopAIChat.Analytics.push('lead_form_shown', { hook: hook, reshown: true });
          try { sessionStorage.setItem('shopAiPendingLeadHook', hook); } catch (e) { /* non-fatal */ }
          ShopAIChat.UI.scrollOfferIntoView(existing, true);
          return;
        }
        // A form restored after a page reload is NOT a new offer — don't re-count it in
        // the GA4 funnel denominator (one ignoring shopper browsing 5 PDPs is 1 offer, not 6).
        if (!(opts && opts.restored)) {
          ShopAIChat.Analytics.push('lead_form_shown', { hook: hook });
        }
        const form = document.createElement('form');
        form.className = 'shop-ai-lead-form';
        form.dataset.hook = hook;
        form.innerHTML =
          '<div class="shop-ai-lead-eyebrow">Save it for later</div>' +
          '<div class="shop-ai-lead-title"></div>' +
          '<div class="shop-ai-lead-subtitle"></div>' +
          '<label class="shop-ai-lead-field"><span>First name <em>optional</em></span><input class="shop-ai-lead-name" type="text" placeholder="First name" autocomplete="given-name"></label>' +
          '<label class="shop-ai-lead-field"><span>Mobile number</span><input class="shop-ai-lead-phone" type="tel" placeholder="859-555-0142" autocomplete="tel" inputmode="tel"></label>' +
          '<button type="button" class="shop-ai-lead-email-toggle">Prefer email instead?</button>' +
          '<label class="shop-ai-lead-field shop-ai-lead-email-field shop-ai-lead-email-hidden"><span>Email</span><input class="shop-ai-lead-email" type="email" placeholder="you@example.com" autocomplete="email" inputmode="email"></label>' +
          '<label class="shop-ai-lead-consent"><input type="checkbox" class="shop-ai-lead-consent-box"> <span></span></label>' +
          '<div class="shop-ai-lead-hint" role="status"></div>' +
          '<div class="shop-ai-lead-trust"></div>' +
          '<button type="submit" class="shop-ai-lead-submit"></button>' +
          '<div class="shop-ai-lead-status" role="status"></div>';
        this.applyCopy(form, hook);
        form.querySelector('.shop-ai-lead-consent span').textContent = this.consentText;
        // Trust microcopy — accurate consent and channel choices at the decision point.
        // textContent-only assembly with dot separators keeps it injection-safe.
        (function (trust) {
          if (!trust) return;
          const dot = function () {
            const d = document.createElement('span');
            d.className = 'shop-ai-trust-dot';
            return d;
          };
          trust.appendChild(document.createTextNode('Consent is not required to buy'));
          trust.appendChild(dot());
          trust.appendChild(document.createTextNode('Reply STOP anytime'));
          trust.appendChild(dot());
          trust.appendChild(document.createTextNode('Phone or email works'));
        })(form.querySelector('.shop-ai-lead-trust'));
        form.querySelector('.shop-ai-lead-email-toggle').addEventListener('click', () => {
          const emailField = form.querySelector('.shop-ai-lead-email-field');
          const emailInput = form.querySelector('.shop-ai-lead-email');
          if (emailField) emailField.classList.remove('shop-ai-lead-email-hidden');
          form.querySelector('.shop-ai-lead-email-toggle').remove();
          if (emailInput) emailInput.focus();
        });
        form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(form, form.dataset.hook || hook); });
        // Survive full page reloads (every product-card click reloads the page) — see fetchChatHistory.
        try { sessionStorage.setItem('shopAiPendingLeadHook', hook); } catch (e) { /* non-fatal */ }
        messagesContainer.appendChild(form);
        ShopAIChat.UI.scrollOfferIntoView(form, true);
      },
      submit: async function(form, hook) {
        const name = form.querySelector('.shop-ai-lead-name').value.trim();
        const phone = form.querySelector('.shop-ai-lead-phone').value.trim();
        const email = form.querySelector('.shop-ai-lead-email').value.trim();
        const consent = form.querySelector('.shop-ai-lead-consent-box').checked;
        const status = form.querySelector('.shop-ai-lead-status');
        const btn = form.querySelector('.shop-ai-lead-submit');
        const hint = form.querySelector('.shop-ai-lead-hint');
        const consentLabel = form.querySelector('.shop-ai-lead-consent');
        if (hint) hint.textContent = '';
        if (consentLabel) consentLabel.classList.remove('shop-ai-consent-attention');
        if (!phone && !email) { status.textContent = 'Please add a phone or email.'; return; }
        if (phone && !consent) {
          // Point at the fix instead of dead-ending at the bottom of the form.
          if (hint) hint.textContent = 'Check the box above so we can legally text you.';
          if (consentLabel) consentLabel.classList.add('shop-ai-consent-attention');
          status.textContent = '';
          return;
        }
        btn.disabled = true; status.textContent = 'Sending…';
        try {
          const res = await fetch(this.BACKEND, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              conversation_id: sessionStorage.getItem('shopAiConversationId'),
              name, phone, email,
              consent_sms: consent,
              consent_text: this.consentText,
              hooks: [hook],
              funnel_id: ShopAIChat.Funnel.id(),
              device_type: ShopAIChat.Funnel.deviceType(),
              attribution: Object.assign({}, ShopAIChat.Attribution.get(), ShopAIChat.Cookies.collect()),
            })
          });
          const data = await res.json();
          if (data.ok) {
            form.classList.add('shop-ai-lead-done');
            try {
              sessionStorage.removeItem('shopAiPendingLeadHook');
              // Captured-state must survive reloads too, or the picks chip / a model
              // re-offer re-renders a fresh form and a resubmit re-texts the shopper.
              sessionStorage.setItem('shopAiLeadDone', '1');
            } catch (e) { /* non-fatal */ }
            form.querySelectorAll('input,button').forEach((el) => { el.disabled = true; });
            status.textContent = phone ? '✓ Sent — check your texts!' : '✓ Sent — keep an eye on your inbox!';
            ShopAIChat.Analytics.push('lead_form_submitted', { hook: hook });
            ShopAIChat.GoogleUserData.send(email, phone, 'lead');
          } else {
            btn.disabled = false; status.textContent = data.error || 'Something went wrong. Try again.';
          }
        } catch (err) {
          btn.disabled = false; status.textContent = 'Network error — please try again.';
        }
      }
    },

    QuizFinaleGate: {
      TIMELINE_BACKEND: 'https://mo-shop-chat-agent-production.up.railway.app/lead/timeline',
      state: null,

      arm: function(data, messagesContainer) {
        this.state = {
          profile: Array.isArray(data && data.profile) ? data.profile.slice(0, 4) : [],
          messagesContainer: messagesContainer,
          requiredPhoneSms: true
        };
      },

      cancel: function(messagesContainer) {
        const root = messagesContainer || (this.state && this.state.messagesContainer);
        if (root) root.querySelectorAll('.shop-ai-quiz-gate').forEach((el) => el.remove());
        this.state = null;
      },

      show: function(data, messagesContainer, opts) {
        if (!messagesContainer || !data) return;
        const gateId = String(data.gate_id || data.id || '');
        const matchCount = Math.max(1, Math.min(3, Number(data.match_count) || 1));
        if (!gateId) return;

        messagesContainer.querySelectorAll('.shop-ai-quiz-gate').forEach((el) => el.remove());
        messagesContainer.querySelectorAll('form.shop-ai-lead-form:not(.shop-ai-support-form):not(.shop-ai-lead-done)')
          .forEach((form) => form.remove());
        try { sessionStorage.removeItem('shopAiPendingLeadHook'); } catch (e) { /* non-fatal */ }

        this.state = Object.assign({}, this.state || {}, {
          gateId: gateId,
          profile: Array.isArray(data.profile) ? data.profile.slice(0, 4) : [],
          matchCount: matchCount,
          primaryBrand: typeof data.primary_brand === 'string' ? data.primary_brand : null,
          requiredPhoneSms: true,
          timelineEnabled: data.timeline_enabled === true,
          leadId: data.lead_id || null,
          timelineSelected: data.purchase_timeline || null,
          messagesContainer: messagesContainer
        });

        const section = document.createElement('section');
        section.className = 'shop-ai-quiz-gate';
        section.setAttribute('aria-labelledby', 'shop-ai-quiz-gate-title');

        const eyebrow = document.createElement('div');
        eyebrow.className = 'shop-ai-quiz-gate-eyebrow';
        eyebrow.textContent = 'YOUR FIT PROFILE';

        const title = document.createElement('h3');
        title.id = 'shop-ai-quiz-gate-title';
        title.className = 'shop-ai-quiz-gate-title';
        title.textContent = 'I found ' + matchCount + (matchCount === 1 ? ' strong match.' : ' strong matches.');

        const profile = document.createElement('div');
        profile.className = 'shop-ai-quiz-gate-profile';
        this.state.profile.forEach(function(label) {
          const tag = document.createElement('span');
          tag.textContent = String(label);
          profile.appendChild(tag);
        });

        const intro = document.createElement('p');
        intro.className = 'shop-ai-quiz-gate-intro';
        intro.textContent = "Your exact models, links, and today's prices are ready. Add your mobile number to reveal them now and get the shortlist by text.";

        const hintArea = document.createElement('div');
        hintArea.className = 'shop-ai-quiz-gate-hint-area';
        if (this.state.primaryBrand) {
          const hintButton = document.createElement('button');
          hintButton.type = 'button';
          hintButton.className = 'shop-ai-quiz-gate-hint-button';
          hintButton.textContent = 'Give me one hint';
          hintButton.addEventListener('click', () => {
            const hint = document.createElement('p');
            hint.className = 'shop-ai-quiz-gate-hint';
            hint.tabIndex = -1;
            hint.textContent = 'Your primary match comes from ' + this.state.primaryBrand + '.';
            hintArea.replaceChildren(hint);
            hint.focus();
            ShopAIChat.Analytics.push('brand_hint_revealed', {});
          }, { once: true });
          hintArea.appendChild(hintButton);
        }

        const form = document.createElement('form');
        form.className = 'shop-ai-quiz-gate-form';
        form.innerHTML =
          '<label class="shop-ai-quiz-gate-field"><span>First name <em>optional</em></span><input class="shop-ai-quiz-gate-name" type="text" autocomplete="given-name" placeholder="First name"></label>' +
          '<label class="shop-ai-quiz-gate-field"><span>Mobile number</span><input class="shop-ai-quiz-gate-phone" type="tel" autocomplete="tel" inputmode="tel" placeholder="859-555-0142" required></label>' +
          '<label class="shop-ai-quiz-gate-consent"><input class="shop-ai-quiz-gate-consent-box" type="checkbox" required><span></span></label>' +
          '<p class="shop-ai-quiz-gate-note">No verification code. Reply STOP anytime. Our local team replies during store hours.</p>' +
          '<button class="shop-ai-quiz-gate-submit" type="submit">Reveal my matches</button>' +
          '<div class="shop-ai-quiz-gate-status" role="status" aria-live="polite"></div>';
        form.querySelector('.shop-ai-quiz-gate-consent span').textContent = ShopAIChat.Lead.consentText;

        let started = false;
        const recordStart = function() {
          if (started) return;
          started = true;
          ShopAIChat.Analytics.push('lead_form_started', { hook: 'save_cart' });
        };
        form.querySelector('.shop-ai-quiz-gate-phone').addEventListener('focus', recordStart, { once: true });
        form.querySelector('.shop-ai-quiz-gate-phone').addEventListener('input', recordStart, { once: true });
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit(form);
        });

        section.appendChild(eyebrow);
        section.appendChild(title);
        if (this.state.profile.length) section.appendChild(profile);
        section.appendChild(intro);
        section.appendChild(hintArea);
        section.appendChild(form);
        messagesContainer.appendChild(section);
        this.state.element = section;

        if (!(opts && opts.restored)) {
          ShopAIChat.Analytics.push('lead_gate_shown', { hook: 'save_cart', match_count: matchCount });
        }
        ShopAIChat.UI.scrollOfferIntoView(section, true);
      },

      submit: async function(form) {
        const state = this.state;
        if (!state || !form) return;
        const name = form.querySelector('.shop-ai-quiz-gate-name').value.trim();
        const phone = form.querySelector('.shop-ai-quiz-gate-phone').value.trim();
        const consent = form.querySelector('.shop-ai-quiz-gate-consent-box').checked;
        const status = form.querySelector('.shop-ai-quiz-gate-status');
        const button = form.querySelector('.shop-ai-quiz-gate-submit');
        if (phone.replace(/\D/g, '').length < 10) {
          status.textContent = 'Enter a valid mobile number.';
          return;
        }
        if (!consent) {
          status.textContent = 'Check the consent box so we can text your matches.';
          return;
        }

        button.disabled = true;
        button.textContent = 'Unlocking matches...';
        status.textContent = '';
        try {
          const response = await fetch(ShopAIChat.Lead.BACKEND, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              conversation_id: sessionStorage.getItem('shopAiConversationId'),
              gate_id: state.gateId,
              name: name,
              phone: phone,
              email: '',
              consent_sms: true,
              consent_text: ShopAIChat.Lead.consentText,
              hooks: ['save_cart'],
              funnel_id: ShopAIChat.Funnel.id(),
              device_type: ShopAIChat.Funnel.deviceType(),
              attribution: Object.assign({}, ShopAIChat.Attribution.get(), ShopAIChat.Cookies.collect())
            })
          });
          const data = await response.json();
          if (!response.ok || !data.ok || !data.quiz_gate_result) {
            button.disabled = false;
            button.textContent = 'Reveal my matches';
            status.textContent = data.error || 'Something went wrong. Try again.';
            return;
          }
          try {
            sessionStorage.setItem('shopAiLeadDone', '1');
            sessionStorage.removeItem('shopAiPendingLeadHook');
          } catch (e) { /* non-fatal */ }
          ShopAIChat.GoogleUserData.send('', phone, 'quiz_gate');
          this.unlock(data.quiz_gate_result, state.messagesContainer, {
            gateId: state.gateId,
            leadId: data.lead_id,
            timelineEnabled: data.timeline_enabled === true,
            restored: false
          });
        } catch (error) {
          button.disabled = false;
          button.textContent = 'Reveal my matches';
          status.textContent = 'Network error - please try again.';
        }
      },

      unlock: function(result, messagesContainer, opts) {
        if (!messagesContainer || !result || typeof result.assistantText !== 'string' || !Array.isArray(result.products)) return;
        if (messagesContainer.querySelector('.shop-ai-quiz-result')) return;
        const prior = this.state || {};
        const meta = Object.assign({}, prior, opts || {});
        messagesContainer.querySelectorAll('.shop-ai-quiz-gate').forEach((el) => el.remove());
        try { sessionStorage.setItem('shopAiLeadDone', '1'); } catch (e) { /* non-fatal */ }

        const success = document.createElement('div');
        success.className = 'shop-ai-quiz-gate-success';
        success.setAttribute('role', 'status');
        success.tabIndex = -1;
        success.textContent = 'Matches unlocked. Your shortlist is also headed to your phone.';
        messagesContainer.appendChild(success);

        const reply = ShopAIChat.Message.add(result.assistantText, 'assistant', messagesContainer);
        reply.classList.add('shop-ai-quiz-result');
        ShopAIChat.UI.displayProductResults(result.products);
        ShopAIChat.Quiz.onProductResults();
        this.state = Object.assign({}, meta, { unlocked: true, messagesContainer: messagesContainer });

        if (!(opts && opts.restored)) {
          ShopAIChat.Analytics.push('results_unlocked', { unlock_method: 'lead_submit' });
        }
        if (meta.timelineEnabled && !meta.timelineSelected && meta.leadId) {
          this.showTimeline(messagesContainer, meta.leadId, meta.gateId);
        }
        if (!(opts && opts.restored)) success.focus();
      },

      showTimeline: function(messagesContainer, leadId, gateId) {
        if (!messagesContainer || !leadId || !gateId || messagesContainer.querySelector('.shop-ai-lead-timeline')) return;
        const section = document.createElement('section');
        section.className = 'shop-ai-lead-timeline';
        const title = document.createElement('h4');
        title.textContent = 'When are you hoping to sleep on a new mattress?';
        const copy = document.createElement('p');
        copy.textContent = 'Optional - this helps our local team prioritize the right next step.';
        const choices = document.createElement('div');
        choices.className = 'shop-ai-lead-timeline-choices';
        const status = document.createElement('div');
        status.className = 'shop-ai-lead-timeline-status';
        status.setAttribute('role', 'status');
        const labels = [
          ['today', 'Today'],
          ['this_week', 'This week'],
          ['this_month', 'This month'],
          ['researching', 'Just researching']
        ];
        labels.forEach(([value, label]) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = label;
          button.addEventListener('click', async () => {
            choices.querySelectorAll('button').forEach((item) => { item.disabled = true; });
            status.textContent = 'Saving...';
            try {
              const response = await fetch(this.TIMELINE_BACKEND, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  lead_id: leadId,
                  conversation_id: sessionStorage.getItem('shopAiConversationId'),
                  gate_id: gateId,
                  timeline: value,
                  funnel_id: ShopAIChat.Funnel.id(),
                  device_type: ShopAIChat.Funnel.deviceType(),
                  traffic_source: ShopAIChat.Funnel.trafficSource()
                })
              });
              const data = await response.json();
              if (!response.ok || !data.ok) throw new Error(data.error || 'Could not save timeline');
              button.classList.add('selected');
              status.textContent = 'Saved. You can keep chatting here anytime.';
              ShopAIChat.Analytics.push('timeline_selected', { timeline: value });
            } catch (error) {
              choices.querySelectorAll('button').forEach((item) => { item.disabled = false; });
              status.textContent = 'Could not save that yet. You can skip it or try again.';
            }
          });
          choices.appendChild(button);
        });
        const skip = document.createElement('button');
        skip.type = 'button';
        skip.className = 'shop-ai-lead-timeline-skip';
        skip.textContent = 'Not now';
        skip.addEventListener('click', () => section.remove());
        section.appendChild(title);
        section.appendChild(copy);
        section.appendChild(choices);
        section.appendChild(skip);
        section.appendChild(status);
        messagesContainer.appendChild(section);
        ShopAIChat.UI.scrollToBottom(true);
      }
    },

    /**
     * Attention nudge: a small dismissible "speech bubble" near the launcher that
     * CYCLES through enticing messages to invite the shopper to chat. First message
     * ~5s after load; each shows ~8s then hides ~20s before the next. Keeps looping
     * while ignored; stops for the session on ✕ dismiss or when the chat is opened.
     * Static copy, no PII.
     */
    Nudge: {
      START_DELAY_MS: 2500,
      SHOW_MS: 8000,
      GAP_MS: 20000,
      MESSAGES: [
        "Not sure where to start? Take the 60-second sleep quiz and I'll narrow it down.",
        "Tell me what your last mattress got wrong. I'll start there.",
        "Side sleeper, hot sleeper, or sharing a bed? I can narrow it fast.",
        "Have a budget and hate mattress jargon? Good. Let's narrow it.",
        "Already comparing two beds? I'll give you the honest tradeoff.",
        "Tell me how you sleep and I'll pick the best fit.",
      ],
      el: null,
      textEl: null,
      _i: 0,
      _timer: null,
      _stopped: false,

      init: function(container) {
        if (!container) return;
        // If the shopper already dismissed or engaged this session, don't loop again.
        if (sessionStorage.getItem('shopAiNudgeStopped') === '1') return;

        // PDP: lead with a product-aware invite, then continue the generic rotation.
        const pdpTitle = ShopAIChat.PageContext.handle() && ShopAIChat.PageContext.shortTitle();
        if (pdpTitle) {
          this.MESSAGES = ["Wondering if the " + pdpTitle + " fits how you sleep? I'll tell you where it fits and where it doesn't."].concat(this.MESSAGES);
        }

        const nudge = document.createElement('div');
        nudge.className = 'shop-ai-nudge';
        nudge.setAttribute('role', 'button');
        nudge.setAttribute('tabindex', '0');
        nudge.setAttribute('aria-label', 'Open chat');

        const text = document.createElement('span');
        text.className = 'shop-ai-nudge-text';

        const close = document.createElement('button');
        close.className = 'shop-ai-nudge-close';
        close.setAttribute('type', 'button');
        close.setAttribute('aria-label', 'Dismiss');
        close.textContent = '×';

        nudge.appendChild(text);
        nudge.appendChild(close);
        container.appendChild(nudge);
        this.el = nudge;
        this.textEl = text;

        const open = () => {
          this.dismiss();
          const win = ShopAIChat.UI.elements && ShopAIChat.UI.elements.chatWindow;
          if (!win || !win.classList.contains('active')) ShopAIChat.UI.toggleChatWindow();
        };
        nudge.addEventListener('click', (e) => { if (e.target === close) return; open(); });
        nudge.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
        });
        close.addEventListener('click', (e) => { e.stopPropagation(); this.dismiss(); });

        this._timer = setTimeout(() => this.cycle(), this.START_DELAY_MS);
      },

      // Show the next message, hide it after SHOW_MS, then loop again after GAP_MS.
      cycle: function() {
        if (this._stopped || !this.el) return;
        // Never pop over an open chat — skip this beat, re-check after the gap.
        const win = ShopAIChat.UI.elements && ShopAIChat.UI.elements.chatWindow;
        if (win && win.classList.contains('active')) {
          this._timer = setTimeout(() => this.cycle(), this.GAP_MS);
          return;
        }
        const msg = this.MESSAGES[this._i % this.MESSAGES.length];
        this.textEl.textContent = msg;
        this.el.setAttribute('aria-label', 'Open chat — ' + msg);
        this._i++;
        this.el.classList.add('active');
        this._timer = setTimeout(() => {
          if (this.el) this.el.classList.remove('active');
          if (!this._stopped) {
            this._timer = setTimeout(() => this.cycle(), this.GAP_MS);
          }
        }, this.SHOW_MS);
      },

      // Stop the loop for this page load (in memory).
      stop: function() {
        this._stopped = true;
        if (this._timer) { clearTimeout(this._timer); this._timer = null; }
        if (this.el) this.el.classList.remove('active');
      },

      // Explicit dismiss (✕) or engagement (chat opened) → stop for the whole session.
      dismiss: function() {
        sessionStorage.setItem('shopAiNudgeStopped', '1');
        this.stop();
      },
    },

    Quiz: {
      _count: 0,
      _recsFired: false,

      // Send text through the existing pipeline (same idiom as product-card buttons).
      sendAsUser: function(text) {
        const input = document.querySelector('.shop-ai-chat-input input');
        const sendButton = document.querySelector('.shop-ai-chat-send');
        if (!input || !sendButton) return;
        input.value = text;
        sendButton.click();
      },

      // Speak first on a genuinely fresh thread. This is local UI copy, not an LLM/API turn,
      // so opening the widget stays instant and does not create a phantom conversation row.
      showOpenGreeting: function(messagesContainer) {
        if (!messagesContainer || messagesContainer.querySelector('.shop-ai-open-greeting')) return;
        const hero = messagesContainer.querySelector('.shop-ai-welcome-hero');
        if (!hero) return; // Returning conversations keep their real history, without a new hello.

        const onPdp = !!ShopAIChat.PageContext.handle();
        const pdpTitle = onPdp && ShopAIChat.PageContext.shortTitle();
        const generic = (window.shopChatConfig && window.shopChatConfig.openingMessage) ||
          "What did your last mattress get wrong: too firm, too hot, or not supportive enough? I'll start there.";
        const text = pdpTitle
          ? "Want an honest fit check on " + pdpTitle + "? I'll ask a few quick questions and tell you where it fits and where it doesn't."
          : generic;
        const message = ShopAIChat.Message.add(text, 'assistant', messagesContainer);
        message.classList.add('shop-ai-open-greeting');
        messagesContainer.insertBefore(message, hero);
      },

      // Welcome hero: animated merchandising welcome state — kicker + greeting + quiz CTA
      // as the hero button (PDP pages get the fit-verdict variant) plus two ghost-chip
      // starters so openers never face a blank input. Keeps the function name so all
      // call sites and the .shop-ai-quiz-chip render-once guard stay intact.
      renderWelcomeChip: function(messagesContainer) {
        if (!messagesContainer || messagesContainer.querySelector('.shop-ai-quiz-chip')) return;
        const onPdp = !!ShopAIChat.PageContext.handle();
        const pdpTitle = onPdp && ShopAIChat.PageContext.shortTitle();
        const self = this;

        const hero = document.createElement('div');
        hero.className = 'shop-ai-welcome-hero';

        const kicker = document.createElement('div');
        kicker.className = 'shop-ai-hero-kicker';
        kicker.textContent = 'YOUR LOCAL MATTRESS AUTHORITY';

        const title = document.createElement('div');
        title.className = 'shop-ai-hero-title';
        title.textContent = pdpTitle ? "Let's see if " + pdpTitle + " fits you." : "Let's find your best-fit bed.";

        const sub = document.createElement('div');
        sub.className = 'shop-ai-hero-sub';
        sub.textContent = onPdp
          ? "Your exact matches and prices unlock by text after the quiz."
          : ((window.shopChatConfig && window.shopChatConfig.heroSub) ||
            "Your exact matches and prices unlock by text after the quiz.");

        hero.appendChild(kicker);
        hero.appendChild(title);
        hero.appendChild(sub);

        const mk = function(label, sendText, analyticsEvent, extraClass) {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'shop-ai-quiz-chip' + (extraClass ? ' ' + extraClass : '');
          chip.textContent = label;
          chip.addEventListener('click', () => {
            if (analyticsEvent) {
              const analyticsParams = analyticsEvent === 'quiz_started'
                ? { entry_point: onPdp ? 'pdp_cta' : 'home_cta' }
                : undefined;
              ShopAIChat.Analytics.push(analyticsEvent, analyticsParams);
            }
            self.sendAsUser(sendText);
          });
          return chip;
        };

        hero.appendChild(mk(
          onPdp ? "Check this bed's fit" : 'Help me narrow it down',
          onPdp ? 'Is this bed right for me? Start the sleep quiz' : 'Help me find my match — start the sleep quiz',
          'quiz_started',
          'shop-ai-chip-hero'
        ));

        const chipsRow = document.createElement('div');
        chipsRow.className = 'shop-ai-hero-chips';
        if (onPdp) {
          chipsRow.appendChild(mk('Compare this bed', 'Compare this bed with the closest alternative and give me the honest tradeoff.', 'chip_compare_pdp_clicked', 'shop-ai-chip-ghost'));
          chipsRow.appendChild(mk('Price & financing', 'What is the price for this bed, and what financing is available?', 'chip_financing_pdp_clicked', 'shop-ai-chip-ghost'));
        } else {
          chipsRow.appendChild(mk("What's on sale?", "Current mattress sale. Jamison, Casper, and Molecule.", 'chip_deals_clicked', 'shop-ai-chip-ghost'));
          chipsRow.appendChild(mk('Delivery & setup', 'How do delivery, setup, and old-mattress haul-away work?', 'chip_delivery_clicked', 'shop-ai-chip-ghost'));
        }
        hero.appendChild(chipsRow);

        messagesContainer.appendChild(hero);
        const chatWindow = ShopAIChat.UI.elements && ShopAIChat.UI.elements.chatWindow;
        if (chatWindow && chatWindow.classList.contains('active')) this.showOpenGreeting(messagesContainer);
      },

      removeWelcomeChip: function() {
        document.querySelectorAll('.shop-ai-quiz-chip, .shop-ai-welcome-hero, .shop-ai-text-picks-chip').forEach((el) => el.remove());
      },

      // Render one question with tappable option chips.
      show: function(data, messagesContainer) {
        if (!messagesContainer) return;
        // Dedupe: if the model repeats the SAME question (server guard should prevent
        // this, but belt-and-suspenders), don't render a second identical card.
        const lastCard = messagesContainer.querySelector('.shop-ai-quiz-card:last-of-type .shop-ai-quiz-question');
        if (lastCard && lastCard.textContent === (data.question || '')) return;
        // Only one active card: retire any previous card's buttons first.
        messagesContainer.querySelectorAll('.shop-ai-quiz-card:not(.answered)').forEach((card) => {
          card.classList.add('answered');
          card.querySelectorAll('button').forEach((b) => { b.disabled = true; });
        });
        const serverStep = Number.isInteger(data.step) && data.step >= 1 && data.step <= 8
          ? data.step
          : this._count + 1;
        this._count = Math.max(this._count, serverStep);
        if (serverStep === 1) ShopAIChat.Funnel.track('quiz_started', { entry_point: 'guided_text' });
        ShopAIChat.Analytics.push('quiz_question_shown', { step: serverStep });
        const card = document.createElement('div');
        card.className = 'shop-ai-quiz-card';
        const step = document.createElement('div');
        step.className = 'shop-ai-quiz-step';
        step.textContent = 'QUESTION ' + serverStep;
        card.appendChild(step);
        const q = document.createElement('div');
        q.className = 'shop-ai-quiz-question';
        q.textContent = data.question || '';
        card.appendChild(q);
        const opts = document.createElement('div');
        opts.className = 'shop-ai-quiz-options';
        (data.options || []).forEach((label) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'shop-ai-quiz-option';
          b.textContent = label;
          b.addEventListener('click', () => {
            if (card.classList.contains('answered')) return;
            card.classList.add('answered');
            b.classList.add('selected');
            card.querySelectorAll('button').forEach((x) => { x.disabled = true; });
            ShopAIChat.Quiz.sendAsUser(label);
          });
          opts.appendChild(b);
        });
        card.appendChild(opts);
        messagesContainer.appendChild(card);
        ShopAIChat.UI.scrollToBottom(true);
      },

      // quiz_recs_shown: first product_results after at least one quiz question.
      onProductResults: function() {
        if (this._count > 0 && !this._recsFired) {
          this._recsFired = true;
          ShopAIChat.Analytics.push('quiz_recs_shown', { questions: this._count });
        }
      },
    },

    Support: {
      BACKEND: 'https://mo-shop-chat-agent-production.up.railway.app/support',
      show: function(category, contact, messagesContainer) {
        // Support and sales capture are mutually exclusive. Remove an unsubmitted sales form
        // and its reload marker before mounting the support handoff.
        messagesContainer.querySelectorAll('form.shop-ai-lead-form:not(.shop-ai-support-form):not(.shop-ai-lead-done)')
          .forEach((form) => form.remove());
        try {
          sessionStorage.removeItem('shopAiPendingLeadHook');
          sessionStorage.setItem('shopAiSupportActive', '1');
        } catch (e) { /* non-fatal */ }
        if (messagesContainer.querySelector('.shop-ai-support-form')) return;
        const c = contact || {};
        const form = document.createElement('form');
        form.className = 'shop-ai-lead-form shop-ai-support-form';
        form.innerHTML =
          '<div class="shop-ai-lead-title">How can our team help?</div>' +
          '<textarea class="shop-ai-support-issue" rows="3" placeholder="Briefly describe your issue (order, delivery, return, etc.)"></textarea>' +
          '<input class="shop-ai-lead-name" type="text" placeholder="Name (optional)" autocomplete="name">' +
          '<input class="shop-ai-lead-phone" type="tel" placeholder="Phone" autocomplete="tel">' +
          '<input class="shop-ai-lead-email" type="email" placeholder="Email" autocomplete="email">' +
          '<button type="submit" class="shop-ai-lead-submit">Send to our team</button>' +
          '<div class="shop-ai-lead-status" role="status"></div>' +
          '<div class="shop-ai-support-contact"></div>';
        const contactParts = [c.email, c.phone, c.hours].filter(Boolean);
        const contactEl = form.querySelector('.shop-ai-support-contact');
        if (contactParts.length) {
          contactEl.textContent = 'Or reach us directly: ' + contactParts.join(' · ');
        } else {
          contactEl.style.display = 'none';
        }
        form.dataset.category = category || 'other';
        form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(form); });
        messagesContainer.appendChild(form);
        ShopAIChat.UI.scrollToBottom(true);
      },
      submit: async function(form) {
        const issue = form.querySelector('.shop-ai-support-issue').value.trim();
        const name = form.querySelector('.shop-ai-lead-name').value.trim();
        const phone = form.querySelector('.shop-ai-lead-phone').value.trim();
        const email = form.querySelector('.shop-ai-lead-email').value.trim();
        const status = form.querySelector('.shop-ai-lead-status');
        const btn = form.querySelector('.shop-ai-lead-submit');
        if (!issue) { status.textContent = 'Please describe your issue.'; return; }
        if (!phone && !email) { status.textContent = 'Add a phone or email so we can reach you.'; return; }
        btn.disabled = true; status.textContent = 'Sending…';
        try {
          const res = await fetch(this.BACKEND, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              conversation_id: sessionStorage.getItem('shopAiConversationId'),
              name, phone, email, issue, category: form.dataset.category,
              attribution: Object.assign({}, ShopAIChat.Attribution.get(), ShopAIChat.Cookies.collect()),
            }),
          });
          const data = await res.json();
          if (data.ok) {
            form.querySelectorAll('input,textarea,button').forEach((el) => { el.disabled = true; });
            status.textContent = '✓ Sent — our team will follow up shortly.';
            ShopAIChat.Analytics.push('support_form_submitted');
          } else { btn.disabled = false; status.textContent = data.error || 'Something went wrong. Try again.'; }
        } catch (err) { btn.disabled = false; status.textContent = 'Network error — please try again.'; }
      }
    },

    /**
     * API communication and data handling
     */
    API: {
      /**
       * Stream a response from the API
       * @param {string} userMessage - User's message text
       * @param {string} conversationId - Conversation ID for context
       * @param {HTMLElement} messagesContainer - The messages container
       */
      streamResponse: async function(userMessage, conversationId, messagesContainer) {
        let currentMessageElement = null;

        try {
          const promptType = window.shopChatConfig?.promptType || "standardAssistant";
          const requestBody = JSON.stringify({
            message: userMessage,
            conversation_id: conversationId,
            prompt_type: promptType,
            // The greeting bubble is local-only for speed. Flag the first reply so the server can
            // add its fixed question as ephemeral model context (never as persisted chat text).
            opening_question: !conversationId && !!messagesContainer.querySelector('.shop-ai-open-greeting'),
            // Cookie-derived ids are merged fresh on every send (e.g. _fbc can
            // appear after first touch); stored first-touch click ids win nothing
            // here because keys don't overlap.
            attribution: Object.assign({}, ShopAIChat.Attribution.get(), ShopAIChat.Cookies.collect()),
            page: ShopAIChat.PageContext.get(),
            funnel_id: ShopAIChat.Funnel.id(),
            device_type: ShopAIChat.Funnel.deviceType(),
            widget_version: document.querySelector('.shop-ai-chat-container')?.dataset.moVersion || null
          });

          try {
            if (!sessionStorage.getItem('shopAiFirstMsgSent')) {
              sessionStorage.setItem('shopAiFirstMsgSent', '1');
              ShopAIChat.Analytics.push('chat_first_message');
            }
          } catch (e) { /* non-fatal */ }

          const streamUrl = 'https://mo-shop-chat-agent-production.up.railway.app/chat';
          const shopId = window.shopId;

          const response = await fetch(streamUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'text/event-stream',
              'X-Shopify-Shop-Id': shopId
            },
            body: requestBody
          });

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          // Create initial message element
          let messageElement = document.createElement('div');
          messageElement.classList.add('shop-ai-message', 'assistant');
          messageElement.textContent = '';
          messageElement.dataset.rawText = '';
          messagesContainer.appendChild(messageElement);
          currentMessageElement = messageElement;

          // Process the stream
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  this.handleStreamEvent(data, currentMessageElement, messagesContainer, userMessage,
                    (newElement) => { currentMessageElement = newElement; });
                } catch (e) {
                  console.error('Error parsing event data:', e, line);
                }
              }
            }
          }
          // Safety net: if the stream ended without an explicit end_turn/message_complete
          // (e.g. the server dropped the connection mid-reply), still release the composer.
          ShopAIChat.UI.setBusy(false);
        } catch (error) {
          console.error('Error in streaming:', error);
          ShopAIChat.UI.removeTypingIndicator();
          ShopAIChat.UI.setBusy(false);
          ShopAIChat.Message.add("Sorry, I couldn't process your request. Please try again later.",
            'assistant', messagesContainer);
        }
      },

      /**
       * Handle stream events from the API
       * @param {Object} data - Event data
       * @param {HTMLElement} currentMessageElement - Current message element being updated
       * @param {HTMLElement} messagesContainer - The messages container
       * @param {string} userMessage - The original user message
       * @param {Function} updateCurrentElement - Callback to update the current element reference
       */
      handleStreamEvent: function(data, currentMessageElement, messagesContainer, userMessage, updateCurrentElement) {
        switch (data.type) {
          case 'id':
            if (data.conversation_id) {
              sessionStorage.setItem('shopAiConversationId', data.conversation_id);
            }
            break;

          case 'chunk': {
            ShopAIChat.UI.removeTypingIndicator();
            const ts = messagesContainer.querySelector('.shop-ai-tool-status');
            if (ts) { if (ts._moSpin) clearInterval(ts._moSpin); ts.remove(); }
            currentMessageElement.dataset.rawText += data.chunk;
            ShopAIChat.Formatting.formatMessageContent(currentMessageElement);
            ShopAIChat.UI.scrollToBottom();
            break;
          }

          case 'message_complete':
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.UI.setBusy(false);
            ShopAIChat.Formatting.formatMessageContent(currentMessageElement);
            ShopAIChat.UI.scrollToBottom();
            break;

          case 'end_turn': {
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.UI.setBusy(false);
            const tsEnd = messagesContainer.querySelector('.shop-ai-tool-status');
            if (tsEnd) { if (tsEnd._moSpin) clearInterval(tsEnd._moSpin); tsEnd.remove(); }
            messagesContainer.querySelectorAll('.shop-ai-message.assistant').forEach((el) => {
              if ((el.dataset.rawText || '').trim() === '' && el.textContent.trim() === '') el.remove();
            });
            break;
          }

          case 'error':
            console.error('Stream error:', data.error);
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.UI.setBusy(false);
            currentMessageElement.textContent = "Sorry, I couldn't process your request. Please try again later.";
            break;

          case 'rate_limit_exceeded':
            console.error('Rate limit exceeded:', data.error);
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.UI.setBusy(false);
            currentMessageElement.textContent = "Sorry, our servers are currently busy. Please try again later.";
            break;

          case 'auth_required':
            // Save the last user message for resuming after authentication
            sessionStorage.setItem('shopAiLastMessage', userMessage || '');
            break;

          case 'quiz_gate_arm':
            if (currentMessageElement) {
              currentMessageElement.dataset.rawText = '';
              currentMessageElement.textContent = '';
            }
            ShopAIChat.QuizFinaleGate.arm(data, messagesContainer);
            break;

          case 'quiz_gate_ready': {
            ShopAIChat.UI.removeTypingIndicator();
            const gateStatus = messagesContainer.querySelector('.shop-ai-tool-status');
            if (gateStatus) gateStatus.remove();
            if (currentMessageElement) {
              currentMessageElement.dataset.rawText = '';
              currentMessageElement.textContent = '';
            }
            ShopAIChat.QuizFinaleGate.show(data, messagesContainer);
            break;
          }

          case 'quiz_gate_cancel':
            ShopAIChat.QuizFinaleGate.cancel(messagesContainer);
            break;

          case 'product_results':
            ShopAIChat.UI.displayProductResults(data.products);
            ShopAIChat.Quiz.onProductResults();
            break;

          case 'tool_use': {
            ShopAIChat.UI.removeTypingIndicator();
            const prevStatus = messagesContainer.querySelector('.shop-ai-tool-status');
            if (prevStatus) { if (prevStatus._moSpin) clearInterval(prevStatus._moSpin); prevStatus.remove(); }

            // A model may narrate a tool call despite the prompt. Remove only a short,
            // standalone progress sentence; specific advice or empathy remains untouched.
            const prelude = String(currentMessageElement && currentMessageElement.dataset.rawText || '').trim();
            if (prelude.length <= 220 && !prelude.includes('\n') &&
                /^(?:i(?:'ll| will| am going to)|i'm going to|let me|checking|searching|finding|pulling up|i need .+\bto (?:answer|check|compare|confirm))\b/i.test(prelude)) {
              currentMessageElement.dataset.rawText = '';
              currentMessageElement.textContent = '';
            }

            const toolName = (String(data.tool_use_message || '').match(/Calling tool:\s*(\S+)/) || [])[1] || '';
            const toolStatuses = {
              search_catalog: 'Matching beds to your needs...',
              get_product_details: "Checking this bed's details...",
              search_shop_policies_and_faqs: 'Checking store details...',
              cart_create: 'Preparing your cart...',
              cart_update: 'Updating your cart...'
            };
            const statusEl = document.createElement('div');
            statusEl.className = 'shop-ai-tool-status';
            statusEl.textContent = toolStatuses[toolName] || 'Checking the details...';
            messagesContainer.appendChild(statusEl);
            ShopAIChat.UI.scrollToBottom();
            break;
          }

          case 'request_lead': {
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.Lead.show(data.hook || 'save_cart', messagesContainer);
            break;
          }

          case 'request_support':
            ShopAIChat.UI.removeTypingIndicator();
            ShopAIChat.Support.show(data.category || 'other', data.contact || {}, messagesContainer);
            break;

          case 'quiz_question':
            ShopAIChat.UI.removeTypingIndicator();
            // Quiz turns should be chip-only. If the model leaked stray prose before the
            // chip (the "thinking block" glitch), clear that in-progress bubble so the
            // shopper sees just the question card. (end_turn prunes the now-empty bubble.)
            if (currentMessageElement) {
              currentMessageElement.dataset.rawText = '';
              currentMessageElement.textContent = '';
            }
            ShopAIChat.Quiz.show(data, messagesContainer);
            break;

          case 'new_message':
            ShopAIChat.Formatting.formatMessageContent(currentMessageElement);
            ShopAIChat.UI.showTypingIndicator();

            // Create new message element for the next response
            const newMessageElement = document.createElement('div');
            newMessageElement.classList.add('shop-ai-message', 'assistant');
            newMessageElement.textContent = '';
            newMessageElement.dataset.rawText = '';
            messagesContainer.appendChild(newMessageElement);

            // Update the current element reference
            updateCurrentElement(newMessageElement);
            break;

          case 'content_block_complete':
            ShopAIChat.UI.showTypingIndicator();
            break;
        }
      },

      /**
       * Fetch chat history from the server
       * @param {string} conversationId - Conversation ID
       * @param {HTMLElement} messagesContainer - The messages container
       */
      fetchChatHistory: async function(conversationId, messagesContainer) {
        try {
          // Show a loading message
          const loadingMessage = document.createElement('div');
          loadingMessage.classList.add('shop-ai-message', 'assistant');
          loadingMessage.textContent = "Loading conversation history...";
          messagesContainer.appendChild(loadingMessage);

          // Fetch history from the server
          const historyUrl = `https://mo-shop-chat-agent-production.up.railway.app/chat?history=true&conversation_id=${encodeURIComponent(conversationId)}`;
          console.log('Fetching history from:', historyUrl);

          const response = await fetch(historyUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            mode: 'cors'
          });

          if (!response.ok) {
            console.error('History fetch failed:', response.status, response.statusText);
            throw new Error('Failed to fetch chat history: ' + response.status);
          }

          const data = await response.json();

          // Remove loading message
          messagesContainer.removeChild(loadingMessage);

          // No messages: fresh thread — show the welcome hero (it carries the greeting).
          if ((!data.messages || data.messages.length === 0) && !data.quiz_gate) {
            try {
              sessionStorage.removeItem('shopAiPendingLeadHook');
              sessionStorage.removeItem('shopAiSupportActive');
            } catch (e) { /* non-fatal */ }
            ShopAIChat.Quiz.renderWelcomeChip(messagesContainer);
            return;
          }

          // Add messages to the UI - filter out tool results
          (data.messages || []).forEach(message => {
            try {
              const messageContents = JSON.parse(message.content);
              for (const contentBlock of messageContents) {
                if (contentBlock.type === 'text') {
                  ShopAIChat.Message.add(contentBlock.text, message.role, messagesContainer);
                }
              }
            } catch (e) {
              ShopAIChat.Message.add(message.content, message.role, messagesContainer);
            }
          });

          if (data.quiz_gate) {
            if (data.quiz_gate.status === 'unlocked' && data.quiz_gate_result) {
              ShopAIChat.QuizFinaleGate.unlock(data.quiz_gate_result, messagesContainer, {
                gateId: data.quiz_gate.id,
                leadId: data.quiz_gate.lead_id,
                timelineEnabled: data.quiz_gate.timeline_enabled === true,
                timelineSelected: data.quiz_gate.purchase_timeline || null,
                restored: true
              });
            } else if (data.quiz_gate.status === 'locked') {
              ShopAIChat.QuizFinaleGate.show({
                id: data.quiz_gate.id,
                profile: data.quiz_gate.profile,
                match_count: data.quiz_gate.match_count,
                primary_brand: data.quiz_gate.primary_brand,
                timeline_enabled: data.quiz_gate.timeline_enabled
              }, messagesContainer, { restored: true });
            }
          }

          // An unsubmitted lead form must survive page reloads (every product-card click is
          // a full reload while the transcript still says "pop your details into the box").
          try {
            const pendingHook = sessionStorage.getItem('shopAiPendingLeadHook');
            if (pendingHook && !data.quiz_gate) ShopAIChat.Lead.show(pendingHook, messagesContainer, { restored: true });
          } catch (e) { /* non-fatal */ }

          // Scroll to bottom
          ShopAIChat.UI.scrollToBottom(true);

        } catch (error) {
          console.error('Error fetching chat history:', error);

          // Remove loading message if it exists
          const loadingMessage = messagesContainer.querySelector('.shop-ai-message.assistant');
          if (loadingMessage && loadingMessage.textContent === "Loading conversation history...") {
            messagesContainer.removeChild(loadingMessage);
          }

          ShopAIChat.Message.add(
            "I couldn't reload this conversation just now. Your session is still saved, so please try again.",
            'assistant',
            messagesContainer
          );
        }
      }
    },

    /**
     * Authentication-related functionality
     */
    Auth: {
      /**
       * Opens an authentication popup window
       * @param {string|HTMLElement} authUrlOrElement - The auth URL or link element that was clicked
       */
      openAuthPopup: function(authUrlOrElement) {
        let authUrl;
        if (typeof authUrlOrElement === 'string') {
          // If a string URL was passed directly
          authUrl = authUrlOrElement;
        } else {
          // If an element was passed
          authUrl = authUrlOrElement.getAttribute('data-auth-url');
          if (!authUrl) {
            console.error('No auth URL found in element');
            return;
          }
        }

        // Open the popup window centered in the screen
        const width = 600;
        const height = 700;
        const left = (window.innerWidth - width) / 2 + window.screenX;
        const top = (window.innerHeight - height) / 2 + window.screenY;

        const popup = window.open(
          authUrl,
          'ShopifyAuth',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        // Focus the popup window
        if (popup) {
          popup.focus();
        } else {
          // If popup was blocked, show a message
          alert('Please allow popups for this site to authenticate with Shopify.');
        }

        // Start polling for token availability
        const conversationId = sessionStorage.getItem('shopAiConversationId');
        if (conversationId) {
          const messagesContainer = document.querySelector('.shop-ai-chat-messages');

          // Add a message to indicate authentication is in progress
          ShopAIChat.Message.add("Authentication in progress. Please complete the process in the popup window.",
            'assistant', messagesContainer);

          this.startTokenPolling(conversationId, messagesContainer);
        }
      },

      /**
       * Start polling for token availability
       * @param {string} conversationId - Conversation ID
       * @param {HTMLElement} messagesContainer - The messages container
       */
      startTokenPolling: function(conversationId, messagesContainer) {
        if (!conversationId) return;

        console.log('Starting token polling for conversation:', conversationId);
        const pollingId = 'polling_' + Date.now();
        sessionStorage.setItem('shopAiTokenPollingId', pollingId);

        let attemptCount = 0;
        const maxAttempts = 30;

        const poll = async () => {
          if (sessionStorage.getItem('shopAiTokenPollingId') !== pollingId) {
            console.log('Another polling session has started, stopping this one');
            return;
          }

          if (attemptCount >= maxAttempts) {
            console.log('Max polling attempts reached, stopping');
            return;
          }

          attemptCount++;

          try {
            const tokenUrl = 'https://mo-shop-chat-agent-production.up.railway.app/auth/token-status?conversation_id=' +
              encodeURIComponent(conversationId);
            const response = await fetch(tokenUrl);

            if (!response.ok) {
              throw new Error('Token status check failed: ' + response.status);
            }

            const data = await response.json();

            if (data.status === 'authorized') {
              console.log('Token available, resuming conversation');
              const message = sessionStorage.getItem('shopAiLastMessage');

              if (message) {
                sessionStorage.removeItem('shopAiLastMessage');
                setTimeout(() => {
                  ShopAIChat.Message.add("Authorization successful! I'm now continuing with your request.",
                    'assistant', messagesContainer);
                  ShopAIChat.API.streamResponse(message, conversationId, messagesContainer);
                  ShopAIChat.UI.showTypingIndicator();
                }, 500);
              }

              sessionStorage.removeItem('shopAiTokenPollingId');
              return;
            }

            console.log('Token not available yet, polling again in 10s');
            setTimeout(poll, 10000);
          } catch (error) {
            console.error('Error polling for token status:', error);
            setTimeout(poll, 10000);
          }
        };

        setTimeout(poll, 2000);
      }
    },

    /**
     * Product-related functionality
     */
    Product: {
      /**
       * Transient add-to-cart request feedback. The actual cart action needs a size
       * and is completed through chat, so this state never claims success early.
       * @param {HTMLButtonElement} button
       */
      flashAddToCart: function(button) {
        try {
          if (!button || button.dataset.moFlashing === '1') return;
          const original = button.dataset.moLabel || button.textContent || 'Add to Cart';
          button.dataset.moLabel = original;
          button.dataset.moFlashing = '1';
          if (button._moFlashT1) clearTimeout(button._moFlashT1);
          if (button._moFlashT2) clearTimeout(button._moFlashT2);

          button.classList.add('is-loading');
          button.disabled = true;
          button.textContent = 'Requesting...';

          button._moFlashT1 = setTimeout(function () {
            button.textContent = 'Checking size...';
          }, 520);

          button._moFlashT2 = setTimeout(function () {
            button.classList.remove('is-loading');
            button.textContent = button.dataset.moLabel || original;
            button.disabled = false;
            button.dataset.moFlashing = '0';
          }, 3000);
        } catch (e) { /* non-fatal */ }
      },

      /**
       * Parse the bot's per-bed reply lines into {name, reason} pairs. The RESPONSE STYLE
       * contract emits one dash line per bed: "- **Brand Model** — $price — plain reason."
       * We take the clause AFTER the price as the reason. Pure, defensive, never throws.
       * @param {string} replyText - the assistant's raw reply markdown
       * @returns {Array<{name:string, reason:string}>}
       */
      extractReasons: function(replyText) {
        const out = [];
        if (!replyText) return out;
        // em-dash / en-dash / space-padded hyphen all act as the segment separator
        const SEP = /\s[—–-]\s/;
        String(replyText).split('\n').forEach(function(raw) {
          const line = String(raw).replace(/^\s*[-*•]\s+/, '').trim();
          const bold = line.match(/\*\*(.+?)\*\*/);
          if (!bold) return;
          const segs = line.split(SEP);
          if (segs.length < 3) return; // need name — price — reason
          let reason = segs.slice(2).join(' — ')
            .replace(/\*\*/g, '').replace(/\s+/g, ' ').replace(/[\s.;,]+$/, '').trim();
          if (reason.length < 3) return;
          if (reason.length > 90) reason = reason.slice(0, 88).replace(/\s+\S*$/, '') + '…';
          out.push({ name: bold[1].replace(/\*/g, '').trim(), reason: reason });
        });
        return out;
      },

      /**
       * Match a product title to its reason from extractReasons(), mirroring the server's
       * name-matching (selectRecommendedProducts): a bolded name and the title match when
       * one contains the other or they share a 2-word run. Returns the reason or null.
       * @param {string} title
       * @param {Array<{name:string, reason:string}>} reasons
       * @returns {string|null}
       */
      reasonFor: function(title, reasons) {
        if (!title || !Array.isArray(reasons) || !reasons.length) return null;
        const norm = function(s) {
          return String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
        };
        const t = norm(title);
        if (!t) return null;
        for (let i = 0; i < reasons.length; i++) {
          const n = norm(reasons[i].name);
          if (!n) continue;
          if (t.indexOf(n) !== -1 || n.indexOf(t) !== -1) return reasons[i].reason;
          const w = n.split(' ').filter(Boolean);
          for (let j = 0; j < w.length - 1; j++) {
            if (t.indexOf(w[j] + ' ' + w[j + 1]) !== -1) return reasons[i].reason;
          }
        }
        return null;
      },

      /**
       * Create a product card element
       * @param {Object} product - Product data
       * @param {string} [reason] - optional one-line "why it fits" note from the bot's reply
       * @returns {HTMLElement} Product card element
       */
      createCard: function(product, reason) {
        const card = document.createElement('div');
        card.classList.add('shop-ai-product-card');

        // Whole-card click → PDP (bigger tap target than the title link alone). Early-return
        // for the add-to-cart button and real links so their own handlers keep working.
        if (product.url) {
          card.classList.add('clickable');
          card.addEventListener('click', function(e) {
            if (e.target.closest('.shop-ai-add-to-cart') || e.target.closest('a')) return;
            window.open(ShopAIChat.Formatting.safeUrl(product.url), '_blank', 'noopener');
          });
        }

        // Create image container
        const imageContainer = document.createElement('div');
        imageContainer.classList.add('shop-ai-product-image');

        // Add product image (only http(s) URLs are allowed; otherwise placeholder)
        const PLACEHOLDER_IMG = 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
        const image = document.createElement('img');
        image.src = /^https?:\/\//i.test(product.image_url || '') ? product.image_url : PLACEHOLDER_IMG;
        image.alt = product.title || 'Product';
        image.onerror = function() {
          this.src = PLACEHOLDER_IMG;
        };
        imageContainer.appendChild(image);
        card.appendChild(imageContainer);

        // Add product info
        const info = document.createElement('div');
        info.classList.add('shop-ai-product-info');

        // Add product title
        const title = document.createElement('h3');
        title.classList.add('shop-ai-product-title');
        title.textContent = product.title;

        // If product has a URL, make the title a link
        if (product.url) {
          const titleLink = document.createElement('a');
          titleLink.href = ShopAIChat.Formatting.safeUrl(product.url);
          titleLink.target = '_blank';
          titleLink.rel = 'noopener noreferrer';
          titleLink.textContent = product.title;
          title.textContent = '';
          title.appendChild(titleLink);
        }

        info.appendChild(title);

        // "Why it fits you" rationale, lifted verbatim from the bot's reply (textContent
        // only — grounding-safe). Rendered only when a reason was matched for this bed.
        if (reason) {
          const reasonEl = document.createElement('p');
          reasonEl.classList.add('shop-ai-product-reason');
          reasonEl.textContent = reason;
          info.appendChild(reasonEl);
        }

        // Add product price. "from $X" gets split typography (small "from", big price);
        // the combined textContent stays byte-identical to product.price — grounding doctrine.
        const price = document.createElement('p');
        price.classList.add('shop-ai-product-price');
        const priceStr = product.price == null ? '' : String(product.price);
        if (/^from /.test(priceStr)) {
          const from = document.createElement('span');
          from.className = 'shop-ai-price-from';
          from.textContent = 'from ';
          const val = document.createElement('span');
          val.className = 'shop-ai-price-value';
          val.textContent = priceStr.slice(5);
          price.appendChild(from);
          price.appendChild(val);
        } else {
          price.textContent = priceStr;
        }
        info.appendChild(price);

        // Add add-to-cart button
        const button = document.createElement('button');
        button.classList.add('shop-ai-add-to-cart');
        button.textContent = 'Add to Cart';
        button.dataset.productId = product.id;

        // Add click handler for the button
        button.addEventListener('click', function() {
          // Add-to-cart routes through the chat send button. While a reply is streaming the
          // send button is disabled (busy state), so a click would no-op — bail before
          // showing request feedback without an actual action.
          const sendButton = document.querySelector('.shop-ai-chat-send');
          if (sendButton && sendButton.disabled) return;

          // Show an honest pending state while the existing chat flow resolves size
          // and performs the real cart action.
          ShopAIChat.Product.flashAddToCart(button);

          // Send message to add this product to cart
          const input = document.querySelector('.shop-ai-chat-input input');
          if (input) {
            input.value = `Add ${product.title} to my cart`;
            // Trigger a click on the send button
            if (sendButton) {
              sendButton.click();
            }
          }
        });

        info.appendChild(button);
        card.appendChild(info);

        return card;
      }
    },

    /**
     * Initialize the chat application
     */
    init: function() {
      // Capture first-touch ad attribution (gclid/fbclid/utm) before anything else
      ShopAIChat.Attribution.capture();
      ShopAIChat.Funnel.init();

      // Initialize UI
      const container = document.querySelector('.shop-ai-chat-container');
      if (!container) return;

      this.UI.init(container);
      this.Nudge.init(container);

      // Check for existing conversation
      const conversationId = sessionStorage.getItem('shopAiConversationId');

      if (conversationId) {
        // Fetch conversation history
        this.API.fetchChatHistory(conversationId, this.UI.elements.messagesContainer);
      } else {
        // No previous conversation: the welcome hero carries the greeting
        try {
          sessionStorage.removeItem('shopAiPendingLeadHook');
          sessionStorage.removeItem('shopAiSupportActive');
        } catch (e) { /* non-fatal */ }
        this.Quiz.renderWelcomeChip(this.UI.elements.messagesContainer);
      }
    }
  };

  // Initialize the application when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      ShopAIChat.init();
    });
  } else {
    ShopAIChat.init();
  }
})();
